/** Needs you (V3Needs): safety notices and decisions waiting for the user, plus what was decided recently. */
import { useState } from "react";
import { Crew } from "../components/Crew.tsx";
import { Shell } from "../components/Shell.tsx";
import { Empty, Loading, Page } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { invalidate, useApi } from "../lib/data.ts";
import { ago } from "../lib/format.ts";
import { Link, navigate } from "../lib/router.tsx";
import type { Character } from "../lib/types.ts";

export interface Need { id: string; account_id: string; ts: number; level: "warn" | "critical"; type: string; message: string; name: string; character: Character }

const LEVEL: Record<Need["level"], [string, string]> = { critical: ["Safety · rings your phone", "text-warn-ink"], warn: ["Notified", "text-muted"] };

export function NeedCard({ need }: { need: Need }) {
  const [busy, setBusy] = useState(false);
  const ack = async (decision: string) => { setBusy(true); try { await api(`/needs/${encodeURIComponent(need.id)}/ack`, { method: "POST", body: { decision } }); invalidate("/needs"); } finally { setBusy(false); } };
  const [lvl, tone] = LEVEL[need.level];
  const action = need.type === "kill_switch" ? "Open the bot to decide what's next" : need.type === "error" ? "Open the bot" : "Open the bot";
  return (
    <article className="card flex flex-col gap-2.5 px-5 py-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <Crew character={need.character} size={26} />
        <span className="text-[13px] text-muted">{need.name} · {ago(need.ts)}</span>
        <span className={`ml-auto text-[13px] ${tone}`}>{lvl}</span>
      </div>
      <div className="text-[16px] font-semibold text-ink-strong">{need.message}</div>
      <div className="mt-1 flex flex-wrap gap-2">
        <button className="btn-primary" onClick={() => navigate(`/bots/${need.account_id}`)}>{action}</button>
        <button className="btn" disabled={busy} onClick={() => void ack("seen")}>{busy ? "Saving…" : "Mark as handled"}</button>
        <Link to={`/chats/${need.account_id}`} className="px-2.5 py-2 text-[14px]">Ask {need.name}</Link>
      </div>
    </article>
  );
}

export function Needs() {
  const { data, loading } = useApi<{ needs: Need[]; recent: { event_id: string; decision: string; ts: number; message: string; name: string }[] }>("/needs", { refreshMs: 30_000 });
  return (
    <Shell crumb="Needs you">
      <Page eyebrow="Decisions and safety" title="Needs you">
        {loading ? <Loading /> : !data!.needs.length ? <Empty title="Nothing needs you">Safety notices and questions from your bots show up here. Safety events also alert you on Telegram if you connect it in Settings.</Empty>
          : <div className="flex flex-col gap-3.5">{data!.needs.map((n) => <NeedCard key={n.id} need={n} />)}</div>}
        {!!data?.recent.length && (
          <section className="flex flex-col gap-2"><h2 className="h2">Recently handled</h2>
            <div className="card overflow-hidden">{data.recent.map((r) => <div key={r.event_id} className="flex gap-3 border-b border-soft px-4 py-3 last:border-0"><span className="flex-1"><strong className="font-semibold">{r.name}</strong> {r.message}</span><span className="text-[13px] text-muted">{ago(r.ts)}</span></div>)}</div>
          </section>
        )}
      </Page>
    </Shell>
  );
}
